import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLazyQuery } from "@apollo/client";
import { gqlMessage } from "gql";
import {
  EPromptType,
  IPromptInput,
  IPromptResponse,
} from "gql/Message/queries";
import { Expand, Loader2, Pencil, Scissors, Sparkles, Text } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import toast from "react-hot-toast";
import useSound from "use-sound";

interface buttonProps {
  readonly icon?: JSX.Element;
  readonly label?: string;
  readonly onClick?: () => void;
}

interface IaButtonProps {
  readonly render?: boolean;
  /**
   * Array de objetos con las propiedades de los botones
   * @default defaultButtonProps
   * @type {buttonProps[]}
   * @example
   * ```tsx
   * buttonProps={[
   *  {
   *   icon: <Home size={16} />,
   *  label: "Inicio",
   * onClick: () => console.log("Navegar a Inicio"),
   * },
   * {
   *  icon: <Settings size={16} />,
   * label: "Configuración",
   *  onClick: () => console.log("Abrir Configuración"),
   * },
   * ]}
   * ```
   **/
  readonly buttonProps?: buttonProps[];
  textToTransform?: string;
  setTextToTransform?: (value: string) => void;
  type: "slate" | "textarea" | "tinyMCE";
  icon?: JSX.Element;
  label?: string;
  disabledCondition?: boolean;
  variant?: "default" | "evaluation" | "create-offer-description";
  externalLoading?: boolean;
  compact?: boolean;
  // promptParams?: IPromptParams[];
}

export default function IaButton(props: IaButtonProps) {
  const { t } = useTranslation("common");
  const [playComplete] = useSound("/sounds/feedback-sound.mp3", {
    volume: 0.25,
  });

  const {
    render = true,
    buttonProps,
    textToTransform,
    type,
    setTextToTransform,
    icon,
    label,
    disabledCondition = false,
    variant = "default",
    externalLoading = false,
    compact = false,
    // promptParams = [],
  } = props;
  const [prompText, { loading }] = useLazyQuery<
    { promptText: IPromptResponse },
    { promptInput: IPromptInput }
  >(gqlMessage.queries.OPEN_AI_TEXT_ENHANCEMENT, {
    fetchPolicy: "network-only",
    onCompleted: () => {
      playComplete();
    },
    onError: () => {
      toast.error(t("Error al mejorar el texto"));
    },
  });

  const performAction = async (action: EPromptType) => {
    if (type === "slate") {
      const recoverText = JSON.parse(textToTransform ?? "");
      const hasImage = recoverText.some((block: any) => block.type === "image");
      const storedBlockedImage: any[] = [];

      if (hasImage) {
        // * we need to iterate over the blocks that type is image and store them
        // * so at the end we can add them to the final blocks
        const blockedImages = recoverText.filter(
          (block: any) => block.type === "image"
        );

        blockedImages.forEach((block: any) => {
          storedBlockedImage.push(block);
        });
      }

      // * we need to iterate over the blocks that type is paragraph
      // * and then iterate over the children to get the text
      // * and then join all the text in a single string

      const text = recoverText
        .filter((block: any) => block.type === "paragraph")
        .map((block: any) => block.children.map((child: any) => child.text))
        .join(" ");

      const { data } = await prompText({
        variables: {
          promptInput: {
            promptText: text,
            promptType: action,
            maxChars: 1000,
          },
        },
      });

      if (data) {
        const { promptText } = data;

        const { aiResponse } = promptText;

        // * we nid to response in slate format
        // * so we need to split the text in blocks

        const blocks = aiResponse.split("\n").map((text) => ({
          type: "paragraph",
          children: [{ text }],
        }));

        // * we need to add the images to the final blocks
        if (storedBlockedImage.length > 0) {
          blocks.push(...storedBlockedImage);
        }

        setTextToTransform && setTextToTransform(JSON.stringify(blocks));
      } else {
        toast.error(t("Error al mejorar el texto"));
      }
    }

    if (type === "textarea") {
      const { data } = await prompText({
        variables: {
          promptInput: {
            promptText: textToTransform || "",
            promptType: action,
            maxChars: 1000,
          },
        },
      });

      if (data) {
        const { promptText } = data;

        const { aiResponse } = promptText;

        setTextToTransform && setTextToTransform(aiResponse);
      } else {
        toast.error(t("Error al mejorar el texto"));
      }
    }

    // if (type === "tinyMCE") {
    //   // * check variants to know what to do
    //   if (variant === "create-offer-description") {
    //     // jobPosition, vendorName, remoteOffer (true/false), offerName
    //     // get values from promptParams and then passes as input to the query

    //     const { data } = await prompText({
    //       variables: {
    //         promptInput: {
    //           promptText: textToTransform || "",
    //           promptType: action,
    //           maxChars: 1000,
    //           promptParams,
    //         },
    //       },
    //     });

    //     if (data) {
    //       const { promptText } = data;

    //       const { aiResponse } = promptText;

    //       setTextToTransform && setTextToTransform(aiResponse);
    //     } else {
    //       toast.error(t("Error al mejorar el texto"));
    //     }
    //   }
    // }
  };

  const defaultButtonProps: buttonProps[] = [
    {
      icon: <Text size={16} />,
      label: t("Mejorar la redacción"),
      onClick: () => performAction(EPromptType.ImproveWriting),
    },
    {
      icon: <Scissors size={16} />,
      label: t("Acortar el texto"),
      onClick: () => performAction(EPromptType.Shorten),
    },
    {
      icon: <Expand size={16} />,
      label: t("Alargar el texto"),
      onClick: () => performAction(EPromptType.Lengthen),
    },
    {
      icon: <Pencil size={16} />,
      label: t("Corrige ortografía y gramática"),
      onClick: () => performAction(EPromptType.FixSpellingAndGrammar),
    },
  ];

  // * check if the textToTransform has text
  // * if not, return false
  // * if the textToTransform has less than 100 characters, return false
  const hasText = () => {
    const blocks = JSON.parse(textToTransform ?? "[]");

    let sumarizedText = "";

    // * we need to check if the blocks has text
    for (const block of blocks) {
      if (block.type === "paragraph") {
        if (block.children.some((child: any) => child.text)) {
          sumarizedText += block.children
            .map((child: any) => child.text)
            .join(" ");
        }
      }
    }

    if (sumarizedText.length > 100) {
      return true;
    }

    return false;
  };

  // * If render is false, return null
  if (!render) return null;

  return (
    <div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className={
              compact
                ? "h-7 w-7 shrink-0 rounded-lg border-slate-200/90 p-0 text-slate-600 hover:bg-slate-50 hover:text-slate-800 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                : "flex items-center gap-2 rounded-full"
            }
            aria-label={compact ? t("Mejorar texto") : "Options"}
            disabled={
              variant === "default"
                ? !textToTransform ||
                  loading ||
                  (type === "slate" && !hasText()) ||
                  (type === "textarea" && textToTransform?.length < 100)
                : disabledCondition
            }
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              icon ?? (
                <Sparkles
                  size={compact ? 14 : 16}
                  className={compact ? "text-slate-600 dark:text-zinc-400" : undefined}
                  color={compact ? undefined : "#549CBB"}
                  strokeWidth={1}
                />
              )
            )}
            {!compact && (
              <span className="text-xs text-slate-600 dark:text-zinc-300">
                {loading || externalLoading
                  ? t("Generando")
                  : label
                    ? label
                    : t("Mejorar texto")}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="z-[99999]"
          align={compact ? "end" : "start"}
          data-compose-floating
        >
          {(buttonProps?.length ? buttonProps : defaultButtonProps).map(
            (item, index) => (
              <DropdownMenuItem
                key={index}
                onClick={item.onClick}
                className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {item.icon && <span className="mr-2">{item.icon}</span>}
                {item.label}
              </DropdownMenuItem>
            )
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
